import { useQueryClient } from '@tanstack/react-query'
import { cloneDeep } from 'lodash'
import { AlertCircle } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useLocation, useParams, useSearchParams } from 'react-router-dom'

import MigrationsList from '../migrations/page'
import PartnerList from '../partner/page'
import { PricingTierSettings } from '../pricing-tier/columns'
import RoleList from '../role/page'
import UserList from '../user/page'

import { OrgActivityTab } from './activity/org-activity-tab'
import { ORG_ACTIVITY_PARAMS } from './activity/use-activity-params'
import { SaveBar } from './detail/detail-section'
import { LoginAsAuditTab } from './detail/login-as-audit-tab'
import { OrganizationHeader } from './detail/organization-header'
import { OrganizationTabBar } from './detail/organization-tab-bar'
import {
  DEFAULT_REPORT,
  DEFAULT_TAB,
  OrganizationReport,
  OrganizationTab,
  REPORT_PARAM,
  resolveOrganizationTab
} from './detail/organization-tabs'
import { OverviewTab } from './detail/overview-tab'
import { PricingTierTab } from './detail/pricing-tier-tab'
import { ReportsTab } from './detail/reports-tab'
import { SettingsTab } from './detail/settings-tab'
import { getUnsavedChanges } from './detail/unsaved-changes'
import { RetrySetupDialog, type RetrySetupOwner } from './retry-setup-dialog'
import {
  isSandboxOrganization,
  SandboxFactoryResetDialog
} from './sandbox-factory-reset-dialog'

import { PricingTierComponents } from '@/api'
import { KpiTile } from '@/components/activity/activity-panel'
import { OrganizationCancelDeletionDialog } from '@/components/organization-cancel-deletion-dialog'
import { OrganizationDeleteDialog } from '@/components/organization-delete-dialog'
import OrganizationFeatureSettings from '@/components/organization-feature-settings'
import OrganizationHubspotData from '@/components/organization-hubspot-data'
import OrganizationSnapshotSchedule from '@/components/organization-snapshot-schedule'
import { SecureProxyWhitelistDialog } from '@/components/secure-proxy-whitelist-dialog'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent } from '@/components/ui/tabs'
import { toast } from '@/components/ui/use-toast'
import { IOrganization } from '@/models'
import { useAuth, useDataPoints, usePricingTiers, useTags } from '@/providers'
import { useOrganizationCleanup } from '@/providers/OrganizationCleanupContextProvider'
import {
  ORGANIZATION_QUERY_IDS,
  useMutationAssignPricingTier,
  useMutationUpdateOrganization,
  useMutationUpdateOrganizationFeatures,
  useMutationUpdatePricingTier,
  useMutationUpdateRole,
  notifyOrganizationSettingUpdate,
  useQueryGetActivePartnerByOrgId,
  useQueryGetAllRoles,
  useQueryGetOrgElasticCluster,
  useQueryGetOrganization,
  useQueryGetOrganizationFeatureSettings,
  useQueryGetOrganizationSettings,
  useQueryGetPricingTierByOrganizationId,
  useQueryUsersByOrgId,
  useQueryGetOrganizations
} from '@/services'
import { PORTAL_WILDCARD_ACTION, getGrantsForPricingTier } from '@/utils'
import { formatNumber } from '@/utils/activity'
import { Permissions, isPermitted } from '@/utils/permission-manager'
import { withChangedSegments } from '@/utils/segments'

const OrganizationDetail = () => {
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const {
    organizationsToCleanup,
    isLoading: isLoadingOrganizationCleanupStatus
  } = useOrganizationCleanup()

  const { orgId } = useParams<{
    orgId: string
  }>()

  const { tab: activeTab, report: activeReport } = resolveOrganizationTab(
    searchParams.get('tab'),
    searchParams.get(REPORT_PARAM)
  )

  const handleTabChange = (value: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)

      if (value === DEFAULT_TAB) next.delete('tab')
      else next.set('tab', value)

      // the Activity tab's own state (period, drawer) doesn't apply to other tabs
      if (value !== 'activity') {
        ORG_ACTIVITY_PARAMS.forEach((param) => next.delete(param))
      }
      if (value !== 'reports') next.delete(REPORT_PARAM)

      return next
    })
  }

  const handleReportChange = (report: OrganizationReport) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)

      next.set('tab', 'reports')
      if (report === DEFAULT_REPORT) next.delete(REPORT_PARAM)
      else next.set(REPORT_PARAM, report)

      return next
    })
  }

  const { data: organization, isLoading: isOrganizationLoading } =
    useQueryGetOrganization(
      {
        orgId: orgId!
      },
      {
        enabled: Boolean(orgId)
      }
    )

  const {
    data: organizationSettings,
    isLoading: isLoadingOrganizationSettings
  } = useQueryGetOrganizationSettings(orgId!, {
    enabled: Boolean(orgId)
  })

  const {
    data: featureSettings,
    isLoading: isLoadingFeatureSettings,
    error: featureSettingsError
  } = useQueryGetOrganizationFeatureSettings(orgId!, {
    enabled: Boolean(orgId)
  })

  const {
    organizationTags: allTags,
    isOrganizationTagsLoading: isAllTagsLoading
  } = useTags()

  const {
    isLoading: isLoadingPricingTiers,
    pricingTierAssignments,
    pricingTiers
  } = usePricingTiers()

  const { userData } = useAuth()

  const { dataPoints, isLoading: isDataPointsLoading } = useDataPoints()

  const [pricingTierState, setPricingTierState] =
    useState<PricingTierComponents.Schemas.PricingTier>()

  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [showCancelDeletionDialog, setShowCancelDeletionDialog] =
    useState(false)
  const [showRetrySetupDialog, setShowRetrySetupDialog] = useState(false)
  const [showFactoryResetDialog, setShowFactoryResetDialog] = useState(false)
  // Set by the create form when the org was created before its login setup finished
  const createdOwner = (
    useLocation().state as { owner?: RetrySetupOwner } | null
  )?.owner
  const [showSecureProxyWhitelistDialog, setShowSecureProxyWhitelistDialog] =
    useState(false)

  const [selectedTags, setSelectedTags] = useState<string[]>(
    organization?.tags ?? []
  )
  const [selectedSegments, setSelectedSegments] = useState<string[]>(
    organization?.segments ?? []
  )
  const [initialSegments, setInitialSegments] = useState<string[]>(
    organization?.segments ?? []
  )
  const [selectedPricingTierId, setSelectedPricingTierId] = useState<
    string | undefined
  >()
  const [initialPricingTier, setInitialPricingTier] = useState<
    string | undefined
  >()
  // this org's overrides as last loaded or saved, to tell whether the draft differs
  const [initialOverrides, setInitialOverrides] =
    useState<Record<string, unknown>>()

  const [organizationState, setOrganizationState] = useState<IOrganization>(
    {} as IOrganization
  )
  // the organization as last loaded or saved, to tell whether the draft differs
  const [savedOrganization, setSavedOrganization] = useState<IOrganization>()

  const [isUpdatingOrganization, setIsUpdatingOrganization] = useState(false)
  const [isUpdatingOrganizationFeatures, setIsUpdatingOrganizationFeatures] =
    useState(false)

  const dataPoint = dataPoints.find((x) => x.org_id.toString() === orgId)

  useEffect(() => {
    if (organization) {
      setSelectedTags(organization?.tags ?? [])
      setSelectedSegments(organization.segments ?? [])
      setInitialSegments(organization.segments ?? [])
      setOrganizationState(organization)
      setSavedOrganization(organization)
    }
  }, [organization])

  const { data: assignedPricingTier, isLoading: isLoadingAssignedPricingTier } =
    useQueryGetPricingTierByOrganizationId(selectedPricingTierId!, orgId!, {
      enabled: Boolean(selectedPricingTierId) && Boolean(orgId)
    })

  useEffect(() => {
    if (assignedPricingTier) {
      handleResetPricingTierState(assignedPricingTier)

      if (assignedPricingTier.id === initialPricingTier) {
        setInitialOverrides(
          cloneDeep(assignedPricingTier.override_settings ?? {})
        )
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignedPricingTier])

  const handleResetPricingTierState = (
    pricingTier: PricingTierComponents.Schemas.PricingTier
  ) => {
    const pricingTierDefaultValues = Object.fromEntries(
      PricingTierSettings.map((field) => [
        field.key,
        pricingTier?.settings?.[field.key] ?? field.default
      ]).filter((x) => x[0] !== 'name')
    )

    setPricingTierState({
      ...pricingTier,
      // overrides belong to the assigned tier; another tier starts without any
      override_settings:
        pricingTier?.id === initialPricingTier
          ? pricingTier.override_settings
          : {},
      settings: pricingTierDefaultValues
    })
  }

  useEffect(() => {
    if (pricingTierAssignments) {
      const pricingTierId = pricingTierAssignments.find(
        (x) => x.organization_id === orgId
      )?.pricing_tier_id

      if (!initialPricingTier) {
        setInitialPricingTier(pricingTierId)
      }
      setSelectedPricingTierId(pricingTierId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, pricingTierAssignments])

  const getMaxBillableUsersLastMonth = () => {
    return (
      (dataPoint?.max_users_last_month ?? 0) -
      (dataPoint?.non_billable_users_last_month ?? 0)
    )
  }

  // if pricing tier setting is overriden use it, otherwise use default pricing tier setting
  const freeUserLimit = +(
    (pricingTierState?.override_settings?.free_user
      ? pricingTierState?.override_settings?.free_user?.quota
      : pricingTierState?.settings?.free_user?.quota) || 0
  )

  const getAdditionalPaidUsersLastMonth = () => {
    // if there's a freeUserLimit set and they have more users last month than freeUserLimit
    if (freeUserLimit > 0 && getMaxBillableUsersLastMonth() > freeUserLimit) {
      return getMaxBillableUsersLastMonth() - freeUserLimit
    }

    return 0
  }

  const { data: activePartners, isLoading: isActivePartnersLoading } =
    useQueryGetActivePartnerByOrgId(orgId!, {
      enabled: Boolean(orgId)
    })

  const { data: endCustomerPortalUsers } = useQueryUsersByOrgId(
    orgId!,
    'END_CUSTOMER_PORTAL',
    {
      enabled: Boolean(orgId)
    }
  )

  const { data: installerPortalUsers } = useQueryUsersByOrgId(
    orgId!,
    'INSTALLER_PORTAL',
    {
      enabled: Boolean(orgId)
    }
  )

  const getActualPartnerUsers = () => {
    let actualPartnerUsers = 0

    if (!isDataPointsLoading && !isActivePartnersLoading) {
      for (const activePartner of activePartners?.data.results ?? []) {
        actualPartnerUsers +=
          dataPoints?.find(
            (dp) => dp.org_id.toString() === activePartner?.partner_org_id
          )?.actual_users ?? 0
      }
    }

    return actualPartnerUsers
  }

  const getMaxPartnerUsersLastMonth = () => {
    let maxPartnerUsers = 0

    if (!isDataPointsLoading && !isActivePartnersLoading) {
      for (const activePartner of activePartners?.data.results ?? []) {
        maxPartnerUsers +=
          dataPoints?.find(
            (dp) => dp.org_id.toString() === activePartner?.partner_org_id
          )?.max_users_last_month ?? 0
      }
    }

    return maxPartnerUsers
  }

  const { data: organizationRoles } = useQueryGetAllRoles(
    { orgId: orgId! },
    {
      enabled: Boolean(orgId)
    }
  )

  const rootRole = organizationRoles?.data.roles?.find((x) => x.slug === 'root')

  const elasticClusterForOrg = useQueryGetOrgElasticCluster(orgId!).data?.data
    ?.cluster

  const updateOrganization = useMutationUpdateOrganization()
  const updateOrganizationFeatures = useMutationUpdateOrganizationFeatures(
    orgId!
  )
  const updateOrganizationPricingTier = useMutationUpdatePricingTier(orgId)
  const assignPricingTier = useMutationAssignPricingTier()
  const updateRole = useMutationUpdateRole(rootRole?.id ?? '', orgId!)

  const handleUpdateOrganization = () => {
    setIsUpdatingOrganization(true)
    if (pricingTierState?.id) {
      assignPricingTier.mutate(
        {
          organization_id: orgId,
          pricing_tier_id: pricingTierState?.id
        },
        {
          onError: (err) => {
            toast({
              title: 'Error',
              description: `Error while assigning pricing tier. ${err?.['message']}`
            })
          }
        }
      )

      updateOrganizationPricingTier.mutate(
        { ...pricingTierState, updated_by: userData?.attributes?.email || '' },
        {
          onError: (err) => {
            toast({
              title: 'Error',
              description: `Error while updating pricing tier. ${err?.['message']}`
            })
          }
        }
      )
    }

    /**
     * This is an best effort attempt at checking if the organization is a Partner, as we
     * don't store any organization metadata that makes this distinction available.
     */
    const isPartnerOrganization =
      assignedPricingTier?.id === '23' ||
      assignedPricingTier?.name?.toLowerCase() === 'partner'

    const pricingTierGrants = getGrantsForPricingTier(
      pricingTierState!,
      isPartnerOrganization
    )
    const rootRoleGrants = (rootRole?.grants || []).filter((grant) => {
      return (
        // derived from the portal pricing keys, so never preserved as a custom grant
        grant.action !== PORTAL_WILDCARD_ACTION &&
        !pricingTierState?.settings?.[grant.action.split(':')[0]] &&
        !pricingTierState?.override_settings?.[grant.action.split(':')[0]] &&
        !pricingTierGrants.find((x) => x.action === grant.action)
      )
    })

    const grants = [...pricingTierGrants, ...rootRoleGrants]

    updateRole.mutate({
      grants: grants,
      name: rootRole!.name,
      slug: rootRole!.slug!,
      id: rootRole!.id!,
      organization_id: rootRole!.organization_id!,
      type: rootRole!.type!,
      pricing_tier: assignedPricingTier?.name
    })

    const organizationUpdate = withChangedSegments(
      {
        ...organizationState,
        tags: selectedTags,
        company_size: organizationState.company_size,
        hubspot_company_id:
          organizationState.hubspot_company_id ||
          (null as unknown as undefined),
        pricing_tier_id: pricingTierState?.id
      },
      selectedSegments,
      initialSegments
    )

    updateOrganization.mutate(organizationUpdate, {
      onSuccess: () => {
        setInitialPricingTier(pricingTierState?.id)
        setInitialSegments(selectedSegments)
        setInitialOverrides(
          cloneDeep(pricingTierState?.override_settings ?? {})
        )
        setSavedOrganization({ ...organizationState, tags: selectedTags })
        setIsUpdatingOrganization(false)
        queryClient.invalidateQueries({
          queryKey: [ORGANIZATION_QUERY_IDS.GET_ORGANIZATION, orgId]
        })
        toast({
          title: 'Success',
          description: 'Organization updated successfully'
        })
      },
      onError: (err) => {
        setIsUpdatingOrganization(false)
        toast({
          title: 'Error',
          description: `Error while updating organization. ${err?.['message']}`
        })
      }
    })
  }

  const handleDiscardChanges = () => {
    if (savedOrganization) setOrganizationState(savedOrganization)
    setSelectedTags(savedOrganization?.tags ?? [])
    setSelectedSegments(initialSegments)

    if (selectedPricingTierId !== initialPricingTier) {
      // the assigned tier reloads from the query and brings its overrides back
      setSelectedPricingTierId(initialPricingTier)
    } else {
      setPricingTierState(
        (prev) =>
          prev && {
            ...prev,
            override_settings: cloneDeep(initialOverrides ?? {})
          }
      )
    }
  }

  const handleOverrideChange = (key: string, value: unknown) => {
    setPricingTierState((prev) => {
      if (!prev) return prev

      const overrides = { ...prev.override_settings }

      if (value === undefined) delete overrides[key]
      else overrides[key] = value

      return { ...prev, override_settings: overrides }
    })
  }

  const unsavedChanges = getUnsavedChanges(
    {
      organization: savedOrganization ?? {},
      tags: savedOrganization?.tags ?? [],
      segments: initialSegments,
      pricingTierId: initialPricingTier,
      overrides: initialOverrides ?? pricingTierState?.override_settings
    },
    {
      organization: organizationState,
      tags: selectedTags,
      segments: selectedSegments,
      pricingTierId: selectedPricingTierId,
      overrides: pricingTierState?.override_settings
    },
    pricingTierState?.settings
  )

  const isDisabledToUpdate = Boolean(
    organizationsToCleanup?.some((x) => x.org_id === orgId) ||
      isLoadingOrganizationCleanupStatus
  )
  const isMarkedForCleanup =
    isDisabledToUpdate && !isLoadingOrganizationCleanupStatus
  const canDelete = isPermitted(Permissions.DELETE_ORG, userData)
  const isSandbox = isSandboxOrganization(organization)

  const { data: productionOrgs } = useQueryGetOrganizations()

  const parentOrganization = organization?.parent_production_org_id
    ? {
        id: organization.parent_production_org_id,
        name: productionOrgs?.find(
          (org) => org.id === organization.parent_production_org_id
        )?.name
      }
    : undefined

  const pricingTierName = pricingTiers?.find(
    (tier) => tier.id === initialPricingTier
  )?.name

  const tabCounts: Partial<Record<OrganizationTab, number | undefined>> = {
    roles: organizationRoles?.data.roles?.length,
    partners: activePartners?.data?.results?.length
  }

  const saveBar = !isDisabledToUpdate && (
    <SaveBar
      changes={unsavedChanges}
      onDiscard={handleDiscardChanges}
      onSave={handleUpdateOrganization}
      saving={isUpdatingOrganization}
    />
  )

  return (
    <Tabs
      className="w-full space-y-4"
      onValueChange={handleTabChange}
      value={activeTab}
    >
      <OrganizationHeader
        canDelete={canDelete}
        isMarkedForCleanup={isMarkedForCleanup}
        isSandbox={isSandbox}
        onDelete={() => setShowDeleteDialog(true)}
        onFactoryReset={() => setShowFactoryResetDialog(true)}
        onOpenSecureProxyWhitelist={() =>
          setShowSecureProxyWhitelistDialog(true)
        }
        organization={organization}
        parentOrganization={parentOrganization}
        pricingTierName={pricingTierName}
      />
      {isMarkedForCleanup && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Marked for cleanup</AlertTitle>
          <AlertDescription className="flex items-center justify-between">
            <span>
              This organization is marked for cleanup. You can not update any
              data on this organization.
            </span>
            <Button
              className="ml-4 shrink-0"
              disabled={!canDelete}
              onClick={() => setShowCancelDeletionDialog(true)}
              size="sm"
              variant="outline"
            >
              Cancel Deletion
            </Button>
          </AlertDescription>
        </Alert>
      )}
      {organization?.login_setup_complete === false && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Setup not finished</AlertTitle>
          <AlertDescription className="flex items-center justify-between">
            <span>
              {
                "This organization's login isn't set up yet, so its owner hasn't been invited."
              }
            </span>
            <Button
              className="ml-4 shrink-0"
              disabled={!isPermitted(Permissions.EDIT_ORG_SETTINGS, userData)}
              onClick={() => setShowRetrySetupDialog(true)}
              size="sm"
              variant="outline"
            >
              Retry setup
            </Button>
          </AlertDescription>
        </Alert>
      )}
      <OrganizationTabBar counts={tabCounts} />
      <TabsContent value="overview">
        {orgId && activeTab === 'overview' && (
          <OverviewTab
            elasticCluster={elasticClusterForOrg}
            isMarkedForCleanup={isMarkedForCleanup}
            isPricingTierLoading={
              isLoadingAssignedPricingTier || isLoadingPricingTiers
            }
            isUsageLoading={isDataPointsLoading || isOrganizationLoading}
            onOpenTab={handleTabChange}
            orgId={orgId}
            organization={organization}
            parentOrganizationName={parentOrganization?.name}
            pricingTier={pricingTierState}
            pricingTierName={pricingTierName}
            usage={{
              actualUsers: dataPoint?.actual_users || 0,
              maxUsersLastMonth: dataPoint?.max_users_last_month || 0,
              maxBillableUsersLastMonth: getMaxBillableUsersLastMonth(),
              additionalPaidUsers: getAdditionalPaidUsersLastMonth(),
              freeUserLimit,
              actualCustomers: dataPoint?.actual_customer || 0,
              maxCustomers: dataPoint?.max_customer || 0,
              activePartnerships: activePartners?.data?.results?.length || 0,
              actualPartnerUsers: getActualPartnerUsers() || 0,
              maxPartnerUsers: getMaxPartnerUsersLastMonth() || 0,
              endCustomerPortalUsers:
                endCustomerPortalUsers?.data?.results?.length || 0,
              installerPortalUsers:
                installerPortalUsers?.data?.results?.length || 0
            }}
          />
        )}
      </TabsContent>
      <TabsContent className="space-y-5" value="settings">
        <SettingsTab
          allTags={allTags}
          canDelete={canDelete}
          disabled={isDisabledToUpdate}
          draft={organizationState}
          elasticCluster={elasticClusterForOrg}
          isSandbox={isSandbox}
          isTagsLoading={isAllTagsLoading}
          onDelete={() => setShowDeleteDialog(true)}
          onDraftChange={(patch) =>
            setOrganizationState((prev) => ({ ...prev, ...patch }))
          }
          onFactoryReset={() => setShowFactoryResetDialog(true)}
          onOpenHubspotData={() => handleTabChange('hubspot-data')}
          onOpenPricingTier={() => handleTabChange('pricing-tier')}
          onOpenSecureProxyWhitelist={() =>
            setShowSecureProxyWhitelistDialog(true)
          }
          organization={organization}
          productionOrganizations={productionOrgs}
          setTags={setSelectedTags}
          tags={selectedTags}
        />
        {saveBar}
      </TabsContent>
      <TabsContent className="space-y-5" value="pricing-tier">
        <PricingTierTab
          disabled={isDisabledToUpdate}
          loading={isLoadingAssignedPricingTier || isLoadingPricingTiers}
          onOverrideChange={handleOverrideChange}
          onResetOverrides={() =>
            setPricingTierState(
              (prev) => prev && { ...prev, override_settings: {} }
            )
          }
          onSelectPricingTier={setSelectedPricingTierId}
          pricingTier={pricingTierState}
          pricingTiers={pricingTiers}
          selectedPricingTierId={selectedPricingTierId}
        />
        {saveBar}
      </TabsContent>
      <TabsContent value="hubspot-data">
        <OrganizationHubspotData
          onOpenSettings={() => handleTabChange('settings')}
          orgId={orgId}
        />
      </TabsContent>
      <TabsContent value="features">
        {featureSettingsError && (
          <p className="mb-4 text-sm text-destructive">
            Failed to load feature list: {featureSettingsError.message}
          </p>
        )}
        <OrganizationFeatureSettings
          aside={
            <OrganizationSnapshotSchedule
              disabled={isDisabledToUpdate}
              orgId={orgId!}
            />
          }
          defaultOpen={true}
          disabled={isDisabledToUpdate}
          featureFlags={featureSettings?.feature_flags ?? []}
          loading={
            isLoadingOrganizationSettings ||
            isLoadingFeatureSettings ||
            isUpdatingOrganizationFeatures
          }
          onChange={(setting: {
            key: string
            value: { [key: string]: boolean }
          }) => {
            setIsUpdatingOrganizationFeatures(true)
            updateOrganizationFeatures.mutate(setting, {
              onSuccess: () => {
                setIsUpdatingOrganizationFeatures(false)
                toast({
                  title: 'Success',
                  description: 'Setting updated successfully'
                })
                notifyOrganizationSettingUpdate({
                  orgId: orgId!,
                  key: setting.key,
                  value: setting.value,
                  userEmail:
                    userData?.attributes['email'] ??
                    JSON.stringify(userData?.attributes),
                  orgName: organization?.name ?? ''
                }).catch((error) => {
                  console.error(
                    'Failed to notify organization setting update',
                    error
                  )
                })
              },
              onError: (err) => {
                setIsUpdatingOrganizationFeatures(false)
                toast({
                  title: 'Error',
                  description: `Error while updating setting. ${err?.['message']}`
                })
              }
            })
          }}
          settings={organizationSettings?.data ?? {}}
        />
      </TabsContent>
      <TabsContent value="migrations">
        <MigrationsList
          disabled={isDisabledToUpdate}
          organizationId={organization?.id}
        />
      </TabsContent>
      <TabsContent value="users">
        <UserList
          loginAsDisabled={isDisabledToUpdate}
          organizationId={organization?.id}
        />
      </TabsContent>
      <TabsContent value="activity">
        {orgId && activeTab === 'activity' && (
          <OrgActivityTab orgId={orgId} orgName={organization?.name} />
        )}
      </TabsContent>
      <TabsContent value="roles">
        <RoleList
          disabled={isDisabledToUpdate}
          organizationId={organization?.id}
        />
      </TabsContent>
      <TabsContent className="space-y-4" value="partners">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <KpiTile
            hint="Partner organizations working with this one"
            isLoading={isActivePartnersLoading}
            label="Active partnerships"
            value={formatNumber(activePartners?.data?.results?.length ?? 0)}
          />
          <KpiTile
            hint="Across all partner organizations"
            isLoading={isActivePartnersLoading || isDataPointsLoading}
            label="Partner users now"
            value={formatNumber(getActualPartnerUsers())}
          />
          <KpiTile
            hint="Sum of each partner's peak last month"
            isLoading={isActivePartnersLoading || isDataPointsLoading}
            label="Max partner users last month"
            value={formatNumber(getMaxPartnerUsersLastMonth())}
          />
        </div>
        <PartnerList />
      </TabsContent>
      <TabsContent value="login-as-audit">
        {orgId && activeTab === 'login-as-audit' && (
          <LoginAsAuditTab orgId={orgId} />
        )}
      </TabsContent>
      <TabsContent value="reports">
        {orgId && activeTab === 'reports' && (
          <ReportsTab
            onReportChange={handleReportChange}
            orgId={orgId}
            report={activeReport}
          />
        )}
      </TabsContent>
      {isSandbox && (
        <SandboxFactoryResetDialog
          onOpenChange={setShowFactoryResetDialog}
          open={showFactoryResetDialog}
          organization={organization}
        />
      )}
      <RetrySetupDialog
        defaultOwner={createdOwner}
        onOpenChange={setShowRetrySetupDialog}
        onSetupComplete={() =>
          queryClient.invalidateQueries({
            queryKey: [ORGANIZATION_QUERY_IDS.GET_ORGANIZATION, orgId]
          })
        }
        open={showRetrySetupDialog}
        organization={organization}
      />
      <OrganizationCancelDeletionDialog
        onCancelDeletion={() => {
          queryClient.invalidateQueries({
            queryKey: [ORGANIZATION_QUERY_IDS.GET_ORGANIZATIONS_TO_CLEANUP]
          })
          updateOrganization.mutate(
            {
              id: orgId,
              tags: selectedTags.filter((tag) => tag !== 'Marked for Cleanup')
            },
            {
              onSuccess: () => {
                queryClient.invalidateQueries({
                  queryKey: [ORGANIZATION_QUERY_IDS.GET_ORGANIZATION, orgId]
                })
              }
            }
          )
        }}
        onOpenChange={setShowCancelDeletionDialog}
        open={showCancelDeletionDialog}
        organization={organization}
      />
      <OrganizationDeleteDialog
        onDelete={() => {
          queryClient.invalidateQueries({
            queryKey: [ORGANIZATION_QUERY_IDS.GET_ORGANIZATIONS_TO_CLEANUP]
          })
          updateOrganization.mutate(
            {
              id: orgId,
              tags: [...selectedTags, 'Marked for Cleanup']
            },
            {
              onSuccess: () => {
                queryClient.invalidateQueries({
                  queryKey: [ORGANIZATION_QUERY_IDS.GET_ORGANIZATION, orgId]
                })
              }
            }
          )
        }}
        onOpenChange={setShowDeleteDialog}
        open={showDeleteDialog}
        organization={organization}
      />
      {organization?.id && showSecureProxyWhitelistDialog && (
        <SecureProxyWhitelistDialog
          onOpenChange={setShowSecureProxyWhitelistDialog}
          open={showSecureProxyWhitelistDialog}
          orgId={organization.id}
        />
      )}
    </Tabs>
  )
}

export default OrganizationDetail
